import React, { useState, useEffect } from 'react';
import '../css/transactionManagement.css';

const TransactionManagement = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({
    status: 'all',
    search: '',
    page: 1,
    limit: 20
  });
  const [stats, setStats] = useState(null);

  // Fetch transactions
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      
      if (filter.status !== 'all') {
        queryParams.append('status', filter.status);
      }
      if (filter.search) {
        queryParams.append('search', filter.search);
      }
      queryParams.append('page', filter.page);
      queryParams.append('limit', filter.limit);

      const response = await fetch(`/api/admin/transactions?${queryParams}`);
      const data = await response.json();

      if (data.success) {
        setTransactions(data.data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch transactions');
      console.error('Fetch transactions error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch transaction statistics
  const fetchStats = async () => {
    try {
      const response = await fetch('/api/admin/transactions/stats');
      const data = await response.json();
      
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Fetch stats error:', err);
    }
  };

  useEffect(() => {
    fetchTransactions();
    fetchStats();
  }, [filter]);

  const handleFilterChange = (key, value) => {
    setFilter(prev => ({
      ...prev,
      [key]: value,
      page: key === 'status' || key === 'search' ? 1 : prev.page
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'paid':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'warning';
      default:
        return 'pending';
    }
  };

  const formatAmount = (amount, currency = 'PHP') => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: currency === 'PHP' ? 'PHP' : 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="transaction-management">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="transaction-management">
      <div className="transaction-header">
        <h2>Transaction Management</h2>
        <p>Monitor and manage payment transactions</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Transactions</div>
          </div>
          <div className="stat-card success">
            <div className="stat-value">{stats.byStatus?.completed?.count || 0}</div>
            <div className="stat-label">Completed</div>
          </div>
          <div className="stat-card error">
            <div className="stat-value">{stats.byStatus?.failed?.count || 0}</div>
            <div className="stat-label">Failed</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-value">{stats.byStatus?.cancelled?.count || 0}</div>
            <div className="stat-label">Cancelled</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="transaction-filters">
        <div className="filter-group">
          <label>Status:</label>
          <select 
            value={filter.status} 
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Transaction ID, Reference, Customer..."
            value={filter.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>

        <button className="refresh-btn" onClick={fetchTransactions}>
          <i className="fas fa-sync-alt"></i>
          Refresh
        </button>
      </div>

      {/* Transaction List */}
      {error ? (
        <div className="error-state">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      ) : (
        <div className="transaction-list">
          {transactions.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-receipt"></i>
              <p>No transactions found</p>
            </div>
          ) : (
            <div className="transaction-table">
              <div className="table-header">
                <div>Transaction ID</div>
                <div>Reference</div>
                <div>Customer</div>
                <div>Amount</div>
                <div>Status</div>
                <div>Date</div>
              </div>
              
              {transactions.map((transaction) => (
                <div key={transaction._id} className="table-row">
                  <div className="transaction-id">
                    {transaction.transactionId}
                  </div>
                  <div className="merchant-ref">
                    {transaction.merchantRefCode}
                  </div>
                  <div className="customer-info">
                    <div className="customer-name">{transaction.customerName}</div>
                    <div className="customer-email">{transaction.customerEmail}</div>
                  </div>
                  <div className="amount">
                    {formatAmount(transaction.amount, transaction.currency)}
                  </div>
                  <div className={`status status-${getStatusColor(transaction.status)}`}>
                    {transaction.status.toUpperCase()}
                  </div>
                  <div className="date">
                    {formatDate(transaction.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionManagement;